/**
 * liveTurns — the client's view of the shared LIVE-TURN mirror (§11 WS-2 of
 * docs/PLAN-mid-turn-fold-in-20260915.md; Miles's ruling T-R13, 2026-09-20: in a
 * group thread Prest0n works in the OPEN).
 *
 * The server writes ONE document per running turn at
 * `group_thread_messages/{threadId}/live/{taskId}` — see
 * functions/src/prest0nSession/liveTurnMirror.ts, the contract this module is the
 * client twin of (the constants below are pinned equal by
 * tests/groupThreads/liveTurns.test.ts): who he is answering, the stage he is on,
 * the last few steps, the branch rail and note COUNTS. Never a note's text, never a
 * role, never `partialAnswer`.
 *
 * This module is PURE — no React, no Firestore, no firebase import — so the card
 * composition is tested on plain objects and the html leaf beside it stays
 * dependency-free (widgetHtml.ts's own rule).
 *
 * What a card is, and is not:
 *  - one card per running turn on the OPEN thread, keyed by task id (a snapshot
 *    that somehow carries a task twice keeps the highest `seq` — last write wins);
 *  - the viewer's OWN turn — a task THIS tab is driving (liveSendRegistry) — is
 *    never a card: the own pending pill already stands for it, stop button and all,
 *    so own docs are returned APART for the host to decorate that pill (a name and
 *    a steps tail). A second device of the same person is not "own" here: it sees
 *    its own work as a card headed "You", which is the 2026-09-17 phone→laptop ask;
 *  - ordered by `startedAtMs`, so the oldest work sits first and a new card lands
 *    under it instead of shuffling the lane;
 *  - a doc whose `updatedAtMs` is older than LIVE_TURN_STALE_MS is dropped — the
 *    client's orphan belt (the server's runs every five minutes with a two-hour
 *    age): a card for a turn that stopped writing ten minutes ago is a lie, not a
 *    spinner;
 *  - at most LIVE_TURN_CARDS_MAX cards, then `hidden` (the branch rail's "+N more").
 */
import { orderBranchEntries, type BranchLaneEntry } from '../globalTools/branchLane';

/** Twins of the server's constants (liveTurnMirror.ts) — pinned equal by the tests. */
export const GROUP_LIVE_TURNS_COLLECTION = 'live';
export const LIVE_TURN_SCHEMA_VERSION = 1;
export const LIVE_RECENT_STEPS_MAX = 6;
/**
 * §12 PR-F1/F2: the server writes EVERY step of a run into `steps[]` — a sliding
 * window of the newest LIVE_STEPS_MAX, with `stepCount` the true total and
 * `stepsDropped` what the window evicted. So a 47-step run reads "step 47", not
 * "3 of a handful" (Miles, 2026-09-21).
 */
export const LIVE_STEPS_MAX = 120;

/** Cards shown at once before "+N more". */
export const LIVE_TURN_CARDS_MAX = 4;
/** A doc that stopped moving for this long is dropped on the client. */
export const LIVE_TURN_STALE_MS = 10 * 60 * 1000;

export type LiveTurnState = 'running' | 'converging';
export type LiveTurnStageKind = 'stage' | 'progress' | 'tool';
export type LiveTurnNoteAdmission = 'asker' | 'participants';

export interface LiveTurnStep {
  label: string;
  atMs: number;
}

/** How a step ended — the rail draws its glyph off this (spinner · check · cross). */
export type LiveTurnStepState = 'running' | 'done' | 'failed';

/**
 * ONE step of a run: its ordinal, what it did, and how it ended. The same shape
 * whether it came from the shared mirror's `steps[]` or from a task document's own
 * `events` (services/prest0nSession/turnSteps.ts), so the rail has one model to draw.
 */
export interface LiveTurnStepRow {
  n: number;
  label: string;
  kind: LiveTurnStageKind;
  toolName?: string;
  atMs: number;
  state: LiveTurnStepState;
}

/** One live-turn doc, parsed tolerantly (any shape in, a well-formed view out). */
export interface LiveTurnView {
  taskId: string;
  threadId: string;
  accountId: string;
  askerUid: string;
  anchorMessageId: string;
  purpose: string;
  state: LiveTurnState;
  startedAtMs: number;
  updatedAtMs: number;
  seq: number;
  orderTs: number;
  stageLabel: string | null;
  stepNumber: number | null;
  stageKind: LiveTurnStageKind | null;
  recentSteps: LiveTurnStep[];
  /** PR-F2: every step the window still holds, oldest first. */
  steps: LiveTurnStepRow[];
  /** PR-F2: every step the run took, the evicted ones included — the honest counter. */
  stepCount: number;
  /** PR-F2: how many the window evicted. */
  stepsDropped: number;
  /** The ledger's own shape, so the rail renders through `buildBranchRailHtml` unchanged. */
  branches: BranchLaneEntry[];
  /** How many notes reached the turn — never what they said. */
  noteCount: number;
  noteAdmission: LiveTurnNoteAdmission;
}

function finite(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Parse one doc. Null when it cannot be a live turn at all (no task, no asker);
 * every other field falls to a safe default rather than throwing — a card drawn
 * from a half-written doc must still be a card.
 */
export function fromLiveTurnDoc(id: string, raw: unknown, nowMs: number = Date.now()): LiveTurnView | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const taskId = str(r.taskId) || str(id);
  const askerUid = str(r.askerUid);
  if (!taskId || !askerUid) return null;
  const startedAtMs = finite(r.startedAtMs) ?? finite(r.updatedAtMs) ?? nowMs;
  const updatedAtMs = finite(r.updatedAtMs) ?? startedAtMs;
  const stage = r.stage && typeof r.stage === 'object' ? (r.stage as Record<string, unknown>) : null;
  const stageLabel = stage ? str(stage.label) || null : null;
  const stepNumber = stage ? finite(stage.stepNumber) : null;
  // The kind is PRESERVED, never flattened: a tool step must still read as a tool step on the card
  // (the rail draws its check/cross off it). A kind this client does not know yet reads as a plain
  // stage, so the parser stays tolerant of a server that is ahead of it.
  const rawStageKind = stage ? str(stage.kind) : '';
  const stageKind: LiveTurnStageKind | null = !stage
    ? null
    : rawStageKind === 'progress' || rawStageKind === 'tool'
      ? rawStageKind
      : 'stage';
  const recentSteps: LiveTurnStep[] = Array.isArray(r.recentSteps)
    ? r.recentSteps
      .map((s): LiveTurnStep | null => {
        if (!s || typeof s !== 'object') return null;
        const e = s as Record<string, unknown>;
        const label = str(e.label);
        return label ? { label, atMs: finite(e.atMs) ?? 0 } : null;
      })
      .filter((s): s is LiveTurnStep => s !== null)
      .slice(-LIVE_RECENT_STEPS_MAX)
    : [];
  // PR-F2: `steps[]` is the run's own history. An older server writes none — then the
  // last six labels (`recentSteps`) stand in, numbered from the end so the rail still
  // reads back in order and the counter never claims more than it can show.
  const parsedSteps: LiveTurnStepRow[] = Array.isArray(r.steps)
    ? r.steps
      .map((raw): LiveTurnStepRow | null => {
        if (!raw || typeof raw !== 'object') return null;
        const e = raw as Record<string, unknown>;
        const label = str(e.label);
        if (!label) return null;
        const n = finite(e.n);
        const kindRaw = str(e.kind);
        const stateRaw = str(e.state);
        const tool = str(e.toolName);
        return {
          n: n !== null && n > 0 ? Math.floor(n) : 0,
          label,
          kind: kindRaw === 'progress' || kindRaw === 'tool' ? kindRaw : 'stage',
          ...(tool ? { toolName: tool } : {}),
          atMs: finite(e.atMs) ?? 0,
          state: stateRaw === 'done' || stateRaw === 'failed' ? stateRaw : 'running',
        };
      })
      .filter((s2): s2 is LiveTurnStepRow => s2 !== null)
      .slice(-LIVE_STEPS_MAX)
    : [];
  const countRaw = finite(r.stepCount);
  const droppedRaw = finite(r.stepsDropped);
  const notes = r.notes && typeof r.notes === 'object' ? (r.notes as Record<string, unknown>) : null;
  const noteCountRaw = notes ? finite(notes.count) : null;
  return {
    taskId,
    threadId: str(r.threadId),
    accountId: str(r.accountId),
    askerUid,
    anchorMessageId: str(r.anchorMessageId),
    purpose: str(r.purpose),
    state: str(r.state) === 'converging' ? 'converging' : 'running',
    startedAtMs,
    updatedAtMs,
    seq: finite(r.seq) ?? 0,
    orderTs: finite(r.orderTs) ?? 0,
    stageLabel,
    stepNumber,
    stageKind,
    recentSteps,
    steps: parsedSteps.length
      ? parsedSteps
      : recentSteps.map((st, i) => ({
        n: recentSteps.length - i === 1 ? recentSteps.length : i + 1,
        label: st.label,
        kind: 'stage' as const,
        atMs: st.atMs,
        state: (i === recentSteps.length - 1 ? 'running' : 'done') as LiveTurnStepState,
      })),
    stepCount: Math.max(
      countRaw !== null && countRaw > 0 ? Math.floor(countRaw) : 0,
      parsedSteps.length ? (parsedSteps[parsedSteps.length - 1]?.n || parsedSteps.length) : recentSteps.length,
    ),
    stepsDropped: droppedRaw !== null && droppedRaw > 0 ? Math.floor(droppedRaw) : 0,
    branches: orderBranchEntries(r.branches, r.branchOrder),
    noteCount: noteCountRaw !== null && noteCountRaw > 0 ? Math.floor(noteCountRaw) : 0,
    noteAdmission: str(r.noteAdmission) === 'participants' ? 'participants' : 'asker',
  };
}

export interface LiveTurnCard extends LiveTurnView {
  /** A task THIS tab is driving — never rendered as a card (the own pill stands for it). */
  own: boolean;
  /** The asker is the viewer (any device) — the header reads "You". */
  self: boolean;
}

export interface LiveTurnCardsView {
  /** The cards to draw, in order, capped. */
  cards: LiveTurnCard[];
  /** The viewer's own running turns (usually none or one) — for decorating the own pill. */
  own: LiveTurnCard[];
  /** How many more cards there are past the cap. */
  hidden: number;
}

export interface ComposeLiveTurnCardsInput {
  docs: ReadonlyArray<LiveTurnView>;
  isOwnTask: (taskId: string) => boolean;
  viewerUid: string | null | undefined;
  nowMs: number;
  max?: number;
  staleMs?: number;
}

/** Pure: the open thread's live docs → the lane's cards. */
export function composeLiveTurnCards(input: ComposeLiveTurnCardsInput): LiveTurnCardsView {
  const max = Math.max(1, Math.floor(input.max ?? LIVE_TURN_CARDS_MAX));
  const staleMs = input.staleMs ?? LIVE_TURN_STALE_MS;
  const byTask = new Map<string, LiveTurnView>();
  // A missing list (an older store shape, a mocked hook) is an empty lane, never a throw.
  for (const d of input.docs ?? []) {
    if (!d || !d.taskId) continue;
    const prev = byTask.get(d.taskId);
    if (!prev || d.seq >= prev.seq) byTask.set(d.taskId, d);
  }
  const live = [...byTask.values()].filter((d) => input.nowMs - d.updatedAtMs <= staleMs);
  live.sort((a, b) => (a.startedAtMs - b.startedAtMs) || (a.taskId < b.taskId ? -1 : a.taskId > b.taskId ? 1 : 0));
  const viewer = (input.viewerUid || '').trim();
  const all: LiveTurnCard[] = live.map((d) => ({
    ...d,
    own: input.isOwnTask(d.taskId),
    self: Boolean(viewer) && d.askerUid === viewer,
  }));
  const own = all.filter((c) => c.own);
  const others = all.filter((c) => !c.own);
  return { cards: others.slice(0, max), own, hidden: Math.max(0, others.length - max) };
}

/** How many step rows a rail shows before its disclosure. */
export const LIVE_TURN_RAIL_VISIBLE_MAX = 3;

export interface LiveTurnRailView {
  /** Newest first, capped at `max` — the rows on screen at rest. */
  visible: LiveTurnStepRow[];
  /** Every step still in the window, newest first — what the disclosure reveals. */
  all: LiveTurnStepRow[];
  /** The run's TRUE total, evicted steps included: "step 47" is honest. */
  stepCount: number;
  /** How many the window evicted — rendered as "+N earlier", never silently lost. */
  dropped: number;
  /** There is more to reveal than `visible` shows. */
  hasMore: boolean;
}

/**
 * The rail's view of a run: newest first, because a person watching wants the step
 * happening NOW at the top and reads DOWN into the past. Pure — the component only
 * draws it (Miles, 2026-09-21: "a way to display this like a rotation transition
 * etc. to go through 47 steps").
 */
export function liveTurnRailView(
  card: Pick<LiveTurnCard, 'steps' | 'stepCount' | 'stepsDropped'>,
  max: number = LIVE_TURN_RAIL_VISIBLE_MAX,
): LiveTurnRailView {
  const cap = Math.max(1, Math.floor(max));
  const all = [...(card.steps ?? [])].reverse();
  const stepCount = Math.max(card.stepCount ?? 0, all.length + (card.stepsDropped ?? 0));
  return {
    visible: all.slice(0, cap),
    all,
    stepCount,
    dropped: Math.max(0, Math.floor(card.stepsDropped ?? 0)),
    hasMore: all.length > cap,
  };
}

/** The earliest start among the docs, or null — the rail chip's clock fallback. */
export function earliestLiveTurnStartMs(docs: ReadonlyArray<Pick<LiveTurnView, 'startedAtMs'>>): number | null {
  let out: number | null = null;
  for (const d of docs ?? []) {
    if (!d || !Number.isFinite(d.startedAtMs) || d.startedAtMs <= 0) continue;
    if (out === null || d.startedAtMs < out) out = d.startedAtMs;
  }
  return out;
}
